/**
 * Hooks para gestión de catálogos del sistema
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  specialtyService,
  maestriaService,
  certificacionService,
  progressClassService,
  positionService,
  financialConceptService,
  academicPeriodService,
  seedCatalogs,
  type Specialty,
  type ProgressClass,
  type Position,
  type FinancialConcept,
  type AcademicPeriod,
  type ClubType,
} from "@/lib/services/catalogs";
import type { Maestria, Certificacion } from "@/types";
import { useAuthStore } from "@/stores/authStore";

// ==================== QUERY KEYS ====================

export const catalogKeys = {
  // Specialties
  specialties: ["specialties"] as const,
  specialtiesByClubType: (clubType: ClubType) =>
    ["specialties", "clubType", clubType] as const,
  specialtiesByCategory: (category: string) =>
    ["specialties", "category", category] as const,
  specialty: (id: string) => ["specialties", id] as const,
  specialtyCategories: ["specialties", "categories"] as const,

  // Maestrias
  maestrias: ["maestrias"] as const,
  maestriasByClubType: (clubType: ClubType) =>
    ["maestrias", "clubType", clubType] as const,
  maestria: (id: string) => ["maestrias", id] as const,

  // Certificaciones
  certificaciones: ["certificaciones"] as const,
  certificacionesByClubType: (clubType: ClubType) =>
    ["certificaciones", "clubType", clubType] as const,
  certificacion: (id: string) => ["certificaciones", id] as const,

  // Progress Classes
  progressClasses: ["progressClasses"] as const,
  progressClassesByClubType: (clubType: ClubType) =>
    ["progressClasses", "clubType", clubType] as const,
  progressClass: (id: string) => ["progressClasses", id] as const,

  // Positions
  positions: ["positions"] as const,
  positionsByLevel: (level: string) => ["positions", "level", level] as const,
  position: (id: string) => ["positions", id] as const,

  // Financial Concepts
  financialConcepts: ["financialConcepts"] as const,
  financialConceptsByType: (type: string) =>
    ["financialConcepts", "type", type] as const,
  financialConceptsByCategory: (category: string) =>
    ["financialConcepts", "category", category] as const,
  financialConcept: (id: string) => ["financialConcepts", id] as const,

  // Academic Periods
  academicPeriods: ["academicPeriods"] as const,
  activePeriod: ["academicPeriods", "active"] as const,
  academicPeriod: (id: string) => ["academicPeriods", id] as const,
};

// ==================== SPECIALTIES ====================

export function useSpecialties() {
  return useQuery({
    queryKey: catalogKeys.specialties,
    queryFn: () => specialtyService.getAll(),
  });
}

export function useSpecialtiesByClubType(clubType: ClubType) {
  return useQuery({
    queryKey: catalogKeys.specialtiesByClubType(clubType),
    queryFn: () => specialtyService.getByClubType(clubType),
    enabled: !!clubType,
  });
}

export function useSpecialtiesByCategory(category: string) {
  return useQuery({
    queryKey: catalogKeys.specialtiesByCategory(category),
    queryFn: () => specialtyService.getByCategory(category),
    enabled: !!category,
  });
}

export function useSpecialtyCategories() {
  return useQuery({
    queryKey: catalogKeys.specialtyCategories,
    queryFn: () => specialtyService.getCategories(),
  });
}

export function useCreateSpecialty() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Specialty, "id" | "createdAt" | "updatedAt">) =>
      specialtyService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.specialties });
      toast.success("Especialidad creada");
    },
    onError: (error) => {
      console.error("Error creating specialty:", error);
      toast.error("Error al crear la especialidad");
    },
  });
}

export function useUpdateSpecialty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Specialty> }) =>
      specialtyService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.specialties });
      queryClient.invalidateQueries({ queryKey: catalogKeys.specialty(id) });
      toast.success("Especialidad actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la especialidad");
    },
  });
}

export function useDeleteSpecialty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => specialtyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.specialties });
      toast.success("Especialidad eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la especialidad");
    },
  });
}

// ==================== MAESTRIAS ====================

export function useMaestrias() {
  return useQuery({
    queryKey: catalogKeys.maestrias,
    queryFn: () => maestriaService.getAll(),
  });
}

export function useMaestriasByClubType(clubType: ClubType) {
  return useQuery({
    queryKey: catalogKeys.maestriasByClubType(clubType),
    queryFn: () => maestriaService.getByClubType(clubType),
    enabled: !!clubType,
  });
}

export function useCreateMaestria() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Maestria, "id" | "createdAt" | "updatedAt">) =>
      maestriaService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.maestrias });
      toast.success("Maestría creada");
    },
    onError: (error) => {
      console.error("Error creating maestria:", error);
      toast.error("Error al crear la maestría");
    },
  });
}

export function useUpdateMaestria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Maestria> }) =>
      maestriaService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.maestrias });
      queryClient.invalidateQueries({ queryKey: catalogKeys.maestria(id) });
      toast.success("Maestría actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la maestría");
    },
  });
}

export function useDeleteMaestria() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => maestriaService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.maestrias });
      toast.success("Maestría eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la maestría");
    },
  });
}

// ==================== CERTIFICACIONES ====================

export function useCertificaciones() {
  return useQuery({
    queryKey: catalogKeys.certificaciones,
    queryFn: () => certificacionService.getAll(),
  });
}

export function useCertificacionesByClubType(clubType: ClubType) {
  return useQuery({
    queryKey: catalogKeys.certificacionesByClubType(clubType),
    queryFn: () => certificacionService.getByClubType(clubType),
    enabled: !!clubType,
  });
}

export function useCreateCertificacion() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Certificacion, "id" | "createdAt" | "updatedAt">) =>
      certificacionService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.certificaciones });
      toast.success("Certificación creada");
    },
    onError: (error) => {
      console.error("Error creating certificacion:", error);
      toast.error("Error al crear la certificación");
    },
  });
}

export function useUpdateCertificacion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Certificacion> }) =>
      certificacionService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.certificaciones });
      queryClient.invalidateQueries({
        queryKey: catalogKeys.certificacion(id),
      });
      toast.success("Certificación actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la certificación");
    },
  });
}

export function useDeleteCertificacion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => certificacionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.certificaciones });
      toast.success("Certificación eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la certificación");
    },
  });
}

// ==================== PROGRESS CLASSES ====================

export function useProgressClasses() {
  return useQuery({
    queryKey: catalogKeys.progressClasses,
    queryFn: () => progressClassService.getAll(),
  });
}

export function useProgressClassesByClubType(clubType: ClubType) {
  return useQuery({
    queryKey: catalogKeys.progressClassesByClubType(clubType),
    queryFn: () => progressClassService.getByClubType(clubType),
    enabled: !!clubType,
  });
}

export function useCreateProgressClass() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<ProgressClass, "id" | "createdAt" | "updatedAt">) =>
      progressClassService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.progressClasses });
      toast.success("Clase creada");
    },
    onError: (error) => {
      console.error("Error creating progress class:", error);
      toast.error("Error al crear la clase");
    },
  });
}

export function useUpdateProgressClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProgressClass> }) =>
      progressClassService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.progressClasses });
      queryClient.invalidateQueries({
        queryKey: catalogKeys.progressClass(id),
      });
      toast.success("Clase actualizada");
    },
    onError: () => {
      toast.error("Error al actualizar la clase");
    },
  });
}

export function useDeleteProgressClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => progressClassService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.progressClasses });
      toast.success("Clase eliminada");
    },
    onError: () => {
      toast.error("Error al eliminar la clase");
    },
  });
}

// ==================== POSITIONS ====================

export function usePositions() {
  return useQuery({
    queryKey: catalogKeys.positions,
    queryFn: () => positionService.getAll(),
  });
}

export function usePositionsByLevel(level: Position["level"]) {
  return useQuery({
    queryKey: catalogKeys.positionsByLevel(level),
    queryFn: () => positionService.getByLevel(level),
    enabled: !!level,
  });
}

export function useCreatePosition() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (data: Omit<Position, "id" | "createdAt" | "updatedAt">) =>
      positionService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.positions });
      toast.success("Cargo creado");
    },
    onError: (error) => {
      console.error("Error creating position:", error);
      toast.error("Error al crear el cargo");
    },
  });
}

export function useUpdatePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Position> }) =>
      positionService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.positions });
      queryClient.invalidateQueries({ queryKey: catalogKeys.position(id) });
      toast.success("Cargo actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el cargo");
    },
  });
}

export function useDeletePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => positionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.positions });
      toast.success("Cargo eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el cargo");
    },
  });
}

// ==================== FINANCIAL CONCEPTS ====================

export function useFinancialConcepts() {
  return useQuery({
    queryKey: catalogKeys.financialConcepts,
    queryFn: () => financialConceptService.getAll(),
  });
}

export function useFinancialConceptsByType(type: "income" | "expense") {
  return useQuery({
    queryKey: catalogKeys.financialConceptsByType(type),
    queryFn: () => financialConceptService.getByType(type),
    enabled: !!type,
  });
}

export function useCreateFinancialConcept() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (
      data: Omit<FinancialConcept, "id" | "createdAt" | "updatedAt">,
    ) => financialConceptService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: catalogKeys.financialConcepts,
      });
      toast.success("Concepto creado");
    },
    onError: (error) => {
      console.error("Error creating financial concept:", error);
      toast.error("Error al crear el concepto");
    },
  });
}

export function useUpdateFinancialConcept() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<FinancialConcept>;
    }) => financialConceptService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: catalogKeys.financialConcepts,
      });
      queryClient.invalidateQueries({
        queryKey: catalogKeys.financialConcept(id),
      });
      toast.success("Concepto actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el concepto");
    },
  });
}

export function useDeleteFinancialConcept() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => financialConceptService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: catalogKeys.financialConcepts,
      });
      toast.success("Concepto eliminado");
    },
    onError: () => {
      toast.error("Error al eliminar el concepto");
    },
  });
}

// ==================== ACADEMIC PERIODS ====================

export function useAcademicPeriods() {
  return useQuery({
    queryKey: catalogKeys.academicPeriods,
    queryFn: () => academicPeriodService.getAll(),
  });
}

export function useActivePeriod() {
  return useQuery({
    queryKey: catalogKeys.activePeriod,
    queryFn: () => academicPeriodService.getActive(),
  });
}

export function useCreateAcademicPeriod() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (
      data: Omit<AcademicPeriod, "id" | "createdAt" | "updatedAt">,
    ) => academicPeriodService.create(data, user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.academicPeriods });
      queryClient.invalidateQueries({ queryKey: catalogKeys.activePeriod });
      toast.success("Periodo creado");
    },
    onError: (error) => {
      console.error("Error creating academic period:", error);
      toast.error("Error al crear el periodo");
    },
  });
}

export function useUpdateAcademicPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AcademicPeriod> }) =>
      academicPeriodService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.academicPeriods });
      queryClient.invalidateQueries({ queryKey: catalogKeys.activePeriod });
      queryClient.invalidateQueries({
        queryKey: catalogKeys.academicPeriod(id),
      });
      toast.success("Periodo actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el periodo");
    },
  });
}

export function useSetActivePeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => academicPeriodService.setActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.academicPeriods });
      queryClient.invalidateQueries({ queryKey: catalogKeys.activePeriod });
      toast.success("Periodo activado");
    },
    onError: () => {
      toast.error("Error al activar el periodo");
    },
  });
}

// ==================== SEED ====================

export function useSeedCatalogs() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: () => seedCatalogs(user?.uid || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogKeys.specialties });
      queryClient.invalidateQueries({ queryKey: catalogKeys.progressClasses });
      queryClient.invalidateQueries({ queryKey: catalogKeys.positions });
      queryClient.invalidateQueries({
        queryKey: catalogKeys.financialConcepts,
      });
      queryClient.invalidateQueries({ queryKey: catalogKeys.academicPeriods });
      toast.success("Catálogos inicializados");
    },
    onError: (error) => {
      console.error("Error seeding catalogs:", error);
      toast.error("Error al inicializar catálogos");
    },
  });
}
